import {
  Button,
  Divider,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle2,
  Tag,
  TagGroup,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ACG_PICKER_GROUPS_SUFFIX,
  DATASET_PERMISSIONS_ROLE_ANNOTATOR,
  DATASET_PERMISSIONS_ROLE_CONSUMER,
  DATASET_PERMISSIONS_ROLE_CONTRIBUTOR,
  DATASET_PERMISSIONS_ROLE_OWNER,
  DATASET_PERMISSIONS_ROLE_RESTRICTED_CONSUMER,
  DATASET_PERMISSIONS_SAVE_BUTTON,
  DATASET_ROLE_OWNER,
  DATASET_ROLE_TOOLTIP_OWNER,
} from '../../../constants/index.js';
import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { useDatasetGroupRole } from '../../../hooks/useDatasetGroupRole/useDatasetGroupRole.js';
import { hasDatasetRole } from '../../../hooks/useMyDatasetRole/hasDatasetRole.js';
import type {
  AccessControlGroup,
  IAccessControlService,
} from '../../../services/accessControlService.interface.js';
import { AccessControlService } from '../../../services/accessControlService.js';
import { withCorrelationId } from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';
import { GroupSearch } from '../../GroupSearch/GroupSearch.js';
import { useTenantRoleSectionStyles } from '../TenantRoleSection/TenantRoleSection.style.js';

import type { DatasetPermissionsTabProps } from './DatasetPermissionsTab.interface.js';
import { useDatasetPermissionsTabStyles } from './DatasetPermissionsTab.style.js';

// ── Internal DatasetRoleSection ───────────────────────────────────────────────

interface DatasetRoleSectionProps {
  label: string;
  groups: AccessControlGroup[];
  service: IAccessControlService;
  readOnly: boolean;
  onGroupAdd: (acg: AccessControlGroup) => void;
  onGroupRemove: (acgId: string) => void;
}

function DatasetRoleSection({
  label,
  groups,
  service,
  readOnly,
  onGroupAdd,
  onGroupRemove,
}: DatasetRoleSectionProps) {
  const sectionStyles = useTenantRoleSectionStyles();

  return (
    <section className={sectionStyles.root} aria-label={label}>
      <div className={sectionStyles.header}>
        <Subtitle2 as="h3" className={sectionStyles.roleLabel}>
          {label}
        </Subtitle2>
        <span className={sectionStyles.groupCount}>
          {groups.length} {ACG_PICKER_GROUPS_SUFFIX}
        </span>
      </div>

      {!readOnly && <GroupSearch service={service} onAdd={onGroupAdd} />}

      {groups.length > 0 && (
        <TagGroup
          className={sectionStyles.groupList}
          onDismiss={
            readOnly
              ? undefined
              : (_e, data) => {
                  onGroupRemove(data.value);
                }
          }
          aria-label={`${label} assigned groups`}
        >
          {groups.map((acg) =>
            readOnly ? (
              <Tag key={acg.id} value={acg.id}>
                {acg.displayName ?? acg.id}
              </Tag>
            ) : (
              <InteractionTag key={acg.id} value={acg.id}>
                <InteractionTagPrimary hasSecondaryAction>
                  {acg.displayName ?? acg.id}
                </InteractionTagPrimary>
                <InteractionTagSecondary
                  aria-label={`Remove ${acg.displayName ?? acg.id}`}
                />
              </InteractionTag>
            ),
          )}
        </TagGroup>
      )}
    </section>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function DatasetPermissionsTab({
  dataSetId,
  myRoles,
}: DatasetPermissionsTabProps) {
  const isOwner = hasDatasetRole(myRoles, DATASET_ROLE_OWNER);
  const styles = useDatasetPermissionsTabStyles();
  const client = useDataHubClient();
  const scenarioFactory = useScenarioFactory();
  const service = useMemo(() => new AccessControlService(client), [client]);

  const {
    ownerGroups: loadedOwner,
    contributorGroups: loadedContributor,
    annotatorGroups: loadedAnnotator,
    consumerGroups: loadedConsumer,
    restrictedConsumerGroups: loadedRestrictedConsumer,
    isLoading,
    error,
    save,
    isSaving,
    saveError,
  } = useDatasetGroupRole({ dataSetId, service });

  const [ownerGroups, setOwnerGroups] = useState<AccessControlGroup[]>([]);
  const [contributorGroups, setContributorGroups] = useState<
    AccessControlGroup[]
  >([]);
  const [annotatorGroups, setAnnotatorGroups] = useState<AccessControlGroup[]>(
    [],
  );
  const [consumerGroups, setConsumerGroups] = useState<AccessControlGroup[]>(
    [],
  );
  const [restrictedConsumerGroups, setRestrictedConsumerGroups] = useState<
    AccessControlGroup[]
  >([]);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setOwnerGroups(loadedOwner);
      setContributorGroups(loadedContributor);
      setAnnotatorGroups(loadedAnnotator);
      setConsumerGroups(loadedConsumer);
      setRestrictedConsumerGroups(loadedRestrictedConsumer);
    }
  }, [
    isLoading,
    loadedOwner,
    loadedContributor,
    loadedAnnotator,
    loadedConsumer,
    loadedRestrictedConsumer,
  ]);

  const handleSave = useCallback(async () => {
    setSaveSuccess(false);
    const scenario = scenarioFactory.newScenario(ScenarioName.PermissionUpdate);
    const scenarioData = { 'Scenario.PermissionScope': 'dataset' };
    try {
      await save(
        {
          owner: ownerGroups.map((g) => g.id),
          contributor: contributorGroups.map((g) => g.id),
          annotator: annotatorGroups.map((g) => g.id),
          consumer: consumerGroups.map((g) => g.id),
          restrictedConsumer: restrictedConsumerGroups.map((g) => g.id),
        },
        { extras: withCorrelationId(scenario.queryId) },
      );
      scenario.complete('success', scenarioData);
      setSaveSuccess(true);
    } catch (error) {
      const classification = classifyScenarioError(error);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      // saveError surfaced via hook
    }
  }, [
    save,
    ownerGroups,
    contributorGroups,
    annotatorGroups,
    consumerGroups,
    restrictedConsumerGroups,
    scenarioFactory,
  ]);

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading permissions..." />
      </div>
    );
  }

  if (error !== null) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{error.message}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={styles.root}>
      {!isOwner && (
        <MessageBar intent="warning">
          <MessageBarBody>{DATASET_ROLE_TOOLTIP_OWNER}</MessageBarBody>
        </MessageBar>
      )}
      <DatasetRoleSection
        label={DATASET_PERMISSIONS_ROLE_OWNER}
        groups={ownerGroups}
        service={service}
        readOnly={!isOwner}
        onGroupAdd={(acg) => setOwnerGroups((prev) => [...prev, acg])}
        onGroupRemove={(id) =>
          setOwnerGroups((prev) => prev.filter((g) => g.id !== id))
        }
      />
      <Divider />
      <DatasetRoleSection
        label={DATASET_PERMISSIONS_ROLE_CONTRIBUTOR}
        groups={contributorGroups}
        service={service}
        readOnly={!isOwner}
        onGroupAdd={(acg) => setContributorGroups((prev) => [...prev, acg])}
        onGroupRemove={(id) =>
          setContributorGroups((prev) => prev.filter((g) => g.id !== id))
        }
      />
      <Divider />
      <DatasetRoleSection
        label={DATASET_PERMISSIONS_ROLE_ANNOTATOR}
        groups={annotatorGroups}
        service={service}
        readOnly={!isOwner}
        onGroupAdd={(acg) => setAnnotatorGroups((prev) => [...prev, acg])}
        onGroupRemove={(id) =>
          setAnnotatorGroups((prev) => prev.filter((g) => g.id !== id))
        }
      />
      <Divider />
      <DatasetRoleSection
        label={DATASET_PERMISSIONS_ROLE_CONSUMER}
        groups={consumerGroups}
        service={service}
        readOnly={!isOwner}
        onGroupAdd={(acg) => setConsumerGroups((prev) => [...prev, acg])}
        onGroupRemove={(id) =>
          setConsumerGroups((prev) => prev.filter((g) => g.id !== id))
        }
      />
      <Divider />
      <DatasetRoleSection
        label={DATASET_PERMISSIONS_ROLE_RESTRICTED_CONSUMER}
        groups={restrictedConsumerGroups}
        service={service}
        readOnly={!isOwner}
        onGroupAdd={(acg) =>
          setRestrictedConsumerGroups((prev) => [...prev, acg])
        }
        onGroupRemove={(id) =>
          setRestrictedConsumerGroups((prev) => prev.filter((g) => g.id !== id))
        }
      />
      {isOwner && (
        <div className={styles.saveRow}>
          <Button
            appearance="primary"
            disabled={isSaving}
            onClick={() => {
              void handleSave();
            }}
          >
            {isSaving ? 'Saving...' : DATASET_PERMISSIONS_SAVE_BUTTON}
          </Button>
          {saveError !== null && (
            <span className={styles.saveError} role="alert">
              {saveError.message}
            </span>
          )}
          {saveSuccess && saveError === null && (
            <span className={styles.saveSuccess} aria-live="polite">
              Permissions saved.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
